import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { Portrait } from "../components/ui/editorial/Portrait.js";
import { useCurator } from "../useCurator.js";
import { trpc } from "../trpc.js";

const LOCAL_PHOTO_KEY = "companion:self-photo";

interface LetterRow {
  id: string;
  status: string;
  subject: { firstName: string } | null;
  sentAt: string | null;
  createdAt: string;
}

/**
 * Home = «редакция пользователя». Никаких захардкоженных Ани/Кати/Лизы —
 * только то, что реально пришло с бекенда (trpc.letters.list). До первого
 * письма показываем waiting-карточку «куратор подбирает».
 *
 * Когда бекенд недоступен (Cloudflare Pages без API URL), список пуст →
 * новый юзер видит только waiting-карточку, что честно.
 */
export function Home(): JSX.Element {
  const navigate = useNavigate();
  const curator = useCurator();
  const lettersQ = trpc.letters.list.useQuery(undefined, { staleTime: 30_000 });

  const [selfPhoto, setSelfPhoto] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setSelfPhoto(window.localStorage.getItem(LOCAL_PHOTO_KEY));
  }, []);

  const letters: LetterRow[] = lettersQ.data?.letters ?? [];
  const latest = letters[0];
  const archive = letters.slice(1);
  const hasLatest = Boolean(latest);

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right="ваша редакция" />
      </div>

      <div className="flex flex-1 flex-col px-[22px] pt-[28px] pb-[28px]">
        <h1 className="font-display font-light leading-[0.95] tracking-[-0.03em] text-ink text-[52px]">
          Реда<em className="italic text-accent">к</em>ция
        </h1>

        <div className="mt-[18px] flex items-center gap-[12px] editorial-meta text-ink/55">
          <span className="h-px flex-1 bg-ink/14" />
          <span>{formatDate(new Date())}</span>
          <span className="h-px flex-1 bg-ink/14" />
        </div>

        {hasLatest ? (
          <ReadyLetterCard
            subjectName={latest?.subject?.firstName ?? "собеседница"}
            letterId={latest?.id ?? ""}
            onOpen={() => {
              if (latest) navigate(`/letter/${latest.id}`);
            }}
          />
        ) : (
          <NewUserCard
            curatorFirstName={curator.firstName}
            onCheck={() => {
              navigate("/waiting");
            }}
          />
        )}

        {archive.length > 0 && (
          <>
            <div className="mt-[36px] flex items-baseline justify-between">
              <div className="editorial-kicker text-ink/55">Архив</div>
              <div className="editorial-meta text-ink/55">
                {archive.length}{" "}
                {pluralize(archive.length, "знакомство", "знакомства", "знакомств")}
              </div>
            </div>

            <ul className="mt-[12px]">
              {archive.map((item, idx) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/letter/${item.id}`);
                    }}
                    className="flex w-full items-center gap-[14px] border-t border-ink/12 py-[14px] text-left hover:opacity-80"
                  >
                    <div className="w-[38px] shrink-0">
                      <Portrait
                        seed={item.subject?.firstName ?? `n-${String(idx)}`}
                        aspect="1 / 1"
                        className="rounded-[8px]"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-light text-[17px] leading-[1.2] text-ink truncate">
                        {item.subject?.firstName ?? "—"}
                      </div>
                      <div className="mt-[2px] editorial-meta text-ink/55 truncate">
                        {statusLabel(item.status)}
                      </div>
                    </div>
                    <div className="flex-none editorial-meta text-ink/40">
                      {whenLabel(item.sentAt ?? item.createdAt)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="px-[22px] pb-[24px]">
        <div className="h-px bg-ink/14" />
        <div className="mt-[14px] mb-[4px] flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              navigate("/curator");
            }}
            className="group flex items-center gap-[10px] py-[6px] pr-[10px]"
          >
            {selfPhoto ? (
              <span
                aria-hidden
                className="relative inline-block h-[18px] w-[18px] overflow-hidden rounded-full border border-ink/25"
              >
                <img src={selfPhoto} alt="" className="h-full w-full object-cover" />
              </span>
            ) : (
              <span
                aria-hidden
                className="relative inline-block h-[14px] w-[14px] rounded-full border border-ink/45"
              >
                <span className="absolute left-1/2 top-[-1px] h-[4px] w-[4px] -translate-x-1/2 rounded-full bg-accent" />
              </span>
            )}
            <span className="editorial-meta text-ink group-hover:text-accent transition-colors">
              {curator.firstName}
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              navigate("/pause");
            }}
            className="group flex items-center gap-[10px] py-[6px] pl-[10px]"
          >
            <span className="editorial-meta text-ink group-hover:text-accent transition-colors">
              редакция
            </span>
            <span aria-hidden className="inline-block h-[10px] w-[10px] border border-ink/45" />
          </button>
        </div>
      </div>
    </Screen>
  );
}

function ReadyLetterCard({
  subjectName,
  onOpen,
}: {
  subjectName: string;
  letterId: string;
  onOpen: () => void;
}): JSX.Element {
  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      onClick={onOpen}
      className="mt-[26px] block w-full text-left rounded-[20px] border border-ink/12 bg-paper-soft px-[22px] pt-[22px] pb-[16px] shadow-paper-sm hover:shadow-paper transition-shadow"
    >
      <div className="editorial-kicker text-accent flex items-center gap-[8px]">
        <motion.span
          aria-hidden
          className="inline-block h-[7px] w-[7px] rounded-full bg-accent"
          animate={{ opacity: [1, 0.35, 1], scale: [1, 0.8, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        />
        <span>Новое письмо · готово</span>
      </div>
      <div className="mt-[12px] flex items-center gap-[14px]">
        <div className="flex-1">
          <div className="font-display font-light leading-[1.05] tracking-[-0.02em] text-ink text-[32px]">
            {subjectName},
            <br />
            <em className="italic text-velvet">письмо от</em>
            <br />
            куратора.
          </div>
        </div>
        <div className="w-[72px] shrink-0">
          <Portrait seed={subjectName} aspect="4 / 5" className="rounded-[10px]" />
        </div>
      </div>
      <div className="mt-[16px] flex items-baseline justify-between border-t border-ink/12 pt-[12px]">
        <span className="font-display font-light text-[15px] text-ink">Прочитать</span>
        <span className="editorial-meta text-accent">→</span>
      </div>
    </motion.button>
  );
}

function NewUserCard({
  curatorFirstName,
  onCheck,
}: {
  curatorFirstName: string;
  onCheck: () => void;
}): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="mt-[26px] rounded-[20px] border border-ink/12 bg-paper-soft px-[22px] pt-[22px] pb-[18px] shadow-paper-sm"
    >
      <div className="editorial-kicker text-accent flex items-center gap-[8px]">
        <motion.span
          aria-hidden
          className="inline-block h-[7px] w-[7px] rounded-full bg-accent"
          animate={{ opacity: [1, 0.35, 1], scale: [1, 0.8, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        />
        <span>{curatorFirstName} подбирает · сейчас</span>
      </div>

      <h2 className="mt-[12px] font-display font-light leading-[1.04] tracking-[-0.02em] text-ink text-[32px]">
        Первое письмо
        <br />
        <em className="italic text-velvet">ещё в работе.</em>
      </h2>

      <p className="mt-[14px] font-display italic font-light text-[15px] leading-[1.5] text-ink/70">
        {curatorFirstName} читает ваши ответы и сейчас перебирает карточки — ищет человека, которому
        не надо объяснять ваши вечера. Письмо придёт, как только редактор закончит.
      </p>

      <div className="mt-[18px] flex items-center gap-[10px] editorial-meta text-ink/50">
        <span aria-hidden className="inline-block h-[6px] w-[6px] rounded-full bg-accent" />
        <span>обычно занимает до вечера</span>
      </div>

      <button
        type="button"
        onClick={onCheck}
        className="mt-[18px] flex w-full items-baseline justify-between border-t border-ink/12 pt-[14px] font-display italic font-light text-[20px] tracking-[-0.01em] text-ink transition-opacity hover:opacity-80"
      >
        <span>посмотреть статус</span>
        <span className="text-accent">→</span>
      </button>
    </motion.div>
  );
}

function formatDate(d: Date): string {
  const days = ["воскресенье", "понедельник", "вторник", "среда", "четверг", "пятница", "суббота"];
  const months = [
    "января",
    "февраля",
    "марта",
    "апреля",
    "мая",
    "июня",
    "июля",
    "августа",
    "сентября",
    "октября",
    "ноября",
    "декабря",
  ];
  return `${days[d.getDay()] ?? ""} · ${String(d.getDate())} ${months[d.getMonth()] ?? ""}`;
}

function statusLabel(status: string): string {
  switch (status) {
    case "sent":
      return "новое · не прочитано";
    case "read":
      return "прочитано";
    case "replied":
      return "вы ответили";
    default:
      return status;
  }
}

function whenLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays <= 0) return "сегодня";
  if (diffDays === 1) return "вчера";
  if (diffDays < 7) return `${String(diffDays)} дн. назад`;
  if (diffDays < 30) return `${String(Math.floor(diffDays / 7))} нед.`;
  return `${String(Math.floor(diffDays / 30))} мес.`;
}

function pluralize(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
